import { useCallback, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';
import {
  useAudioStreamStore,
  useConsumersStore,
  useNotificationStore,
  usePeersStore,
  useProducersStore,
  useRoomActionsStore,
  useRoomStore,
  useVideoStreamStore,
} from '@src/stores';

import { playSound } from '@src/helpers';

import { MEDIA_TYPE } from '@src/constants';

import { ECommonSocketKeys, IHandleUpdatePeerInfo, IPeer, TMediaTypes, TPeerActionCallbackProps } from '@src/types';

const usePeerActions = (currentPeer?: IPeer) => {
  const { consumers } = useConsumersStore(
    useShallow((state) => ({
      consumers: state.consumers,
    }))
  );

  const { peers } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
      updatePeerData: state.updatePeerData,
    }))
  );

  const thereAreParticipants = useMemo(() => consumers.size > 0 || peers?.size > 1, [consumers.size, peers?.size]);

  const { roomId } = useRoomStore(
    useShallow((state) => ({
      roomId: state.roomId,
    }))
  );
  const { updatePeerInfo, raiseHand, lowerHand } = usePeersStore(
    useShallow((state) => ({
      updatePeerInfo: state.updatePeerInfo,
      raiseHand: state.raiseHand,
      lowerHand: state.lowerHand,
    }))
  );

  const { updateAudio } = useAudioStreamStore(
    useShallow((state) => ({
      updateAudio: state.updateAudio,
    }))
  );

  const { producers, producerLabel } = useProducersStore(
    useShallow((state) => ({
      producers: state.producers,
      producerLabel: state.producerLabel,
    }))
  );

  const { onMicClick } = useRoomActionsStore(
    useShallow((state) => ({
      setMicSettings: state.setMicSettings,
      onMicClick: state.micSettings.onMicClick,
    }))
  );

  const setIsAudio = useCallback(
    (peer_id: string, status: boolean) => {
      updatePeerInfo(peer_id, { peer_audio: status });
      updateAudio(peer_id, { muted: !status });
    },
    [updateAudio, updatePeerInfo]
  );

  const pauseProducer = useCallback(
    async (type: TMediaTypes) => {
      if (!producerLabel.has(type)) {
        return console.warn('There is no producer for this type ' + type);
      }

      const producer_id = producerLabel.get(type) || '';
      producers.get(producer_id)?.pause();

      try {
        const response = await SocketService.request('pauseProducer', { producer_id: producer_id });
        console.log('Producer paused', response);
      } catch (error) {
        console.error('Error pausing producer', error);
      }

      switch (type) {
        case 'audioType':
          if (currentPeer) setIsAudio(currentPeer?.peer_info?.peer_id, false);
          break;
        default:
          return;
      }
    },
    [currentPeer, producerLabel, producers, setIsAudio]
  );

  const producerExist = useCallback(
    (type: TMediaTypes) => {
      return producerLabel.has(type);
    },
    [producerLabel]
  );

  const setPeerAudio = useCallback(
    (peer_id: string, status: boolean) => {
      updatePeerInfo(peer_id, { peer_audio: status });
      updateAudio(peer_id, { muted: !status });
    },
    [updateAudio, updatePeerInfo]
  );

  const removeVideoOff = useCallback((peer_id: string) => {
    useVideoStreamStore.getState().removeVideo(peer_id);
    usePeersStore.getState().updatePeerInfo(peer_id, { peer_video: false });
  }, []);

  const checkPeerInfoStatus = useCallback((peer_info: any) => {
    const peer_id = peer_info.peer_id;
    const peer_hand_status = peer_info.peer_hand;
    usePeersStore.getState().updatePeerInfo(peer_id, { ...peer_info });
    !!peer_hand_status ? usePeersStore.getState().raiseHand(peer_id) : usePeersStore.getState().lowerHand(peer_id);
  }, []);

  const handleHideMe = useCallback(
    (isActive?: boolean) => {
      if (currentPeer) {
        const isHideMeActive =
          isActive !== undefined ? isActive : !!useRoomActionsStore?.getState()?.hideSelfSettings?.isActive;
        useVideoStreamStore.getState().updateVideo(currentPeer?.peer_info?.peer_id, { isHideMe: isActive });
        isHideMeActive ? playSound('left') : playSound('joined');
      }
    },
    [currentPeer]
  );

  const setVideoOff = useCallback(
    (peer_info: any, remotePeer = false) => {
      const { peer_id, peer_name, peer_audio, peer_presenter } = peer_info;

      removeVideoOff(peer_id);

      /* TODO: VIDEO_BOX_CONTAINER */
      useVideoStreamStore.getState().appendVideo({
        id: peer_id,
        stream: null,
        peerId: peer_id,
        peerName: peer_name,
        type: 'videoType',
        volume: 0,
        audioVolume: 0,
        autoPlay: true,
        isPresenter: peer_presenter,
        muted: true,
        poster: '../images/loader.gif',
        isVideoOff: true,
        isCurrentUser: !remotePeer,
      });

      if (!remotePeer) {
        useVideoStreamStore.getState().setOriginalStream(null);
        useVideoStreamStore.getState().setModifiedStream(null);
      }

      checkPeerInfoStatus(peer_info);

      remotePeer ? setPeerAudio(peer_id, peer_audio) : setIsAudio(peer_id, peer_audio);

      handleHideMe();
    },
    [checkPeerInfoStatus, handleHideMe, removeVideoOff, setIsAudio, setPeerAudio]
  );

  const sendVideoOff = useCallback(() => {
    SocketService.emit('setVideoOff', currentPeer?.peer_info);
  }, [currentPeer]);

  const handleUpdatePeerInfo = useCallback(
    ({
      peer_name,
      peer_id,
      type,
      status,
      emit = true,
      presenter = false,
      extraData,
      extraFields,
    }: IHandleUpdatePeerInfo) => {
      if (emit) {
        switch (type) {
          case 'audio':
            setIsAudio(peer_id, status);
            break;
          default:
            break;
        }
        const data = {
          room_id: roomId,
          peer_name,
          peer_id,
          type,
          status,
          extraData,
          broadcast: true,
          ...(extraFields && { ...extraFields }),
        };

        SocketService.emit('updatePeerInfo', data);
      } else {
        switch (type) {
          case 'audio':
            setPeerAudio(peer_id, status);
            break;
          case 'recording':
            usePeersStore.getState().updatePeerInfo(peer_id, { peer_recording: !!status });
            break;
          case 'video':
            // NOTE: this will handle peer action for webinar only
            updatePeerInfo(peer_id, { peer_video: status });
            break;
          case 'screenType':
            updatePeerInfo(peer_id, {
              peer_screen: status,
              ...extraData,
            });

            break;
          case 'screen':
            break;
          case 'hand':
            status ? raiseHand(peer_id) : lowerHand(peer_id);
            currentPeer?.peer_info.peer_id === peer_id &&
              useRoomActionsStore.getState().setRaiseHandSettings({ isActive: status });
            if (status && currentPeer?.peer_info.peer_id !== peer_id && peer_name) {
              //trigger the raise hand notification
              useNotificationStore.getState().addRaisedHandNotification(peer_id, peer_name);
              playSound('raiseHand', true);
            }
            break;
          default:
            break;
        }
      }
    },
    [currentPeer?.peer_info.peer_id, lowerHand, raiseHand, roomId, setIsAudio, setPeerAudio, updatePeerInfo]
  );

  const closeProducer = useCallback(
    (type: string) => {
      if (!useProducersStore.getState().producerLabel.has(type)) {
        return console.warn('There is no producer for this type ' + type);
      }

      const producer_id = useProducersStore.getState().producerLabel.get(type) || '';

      const data = {
        peer_name: currentPeer?.peer_info?.peer_name,
        producer_id: producer_id,
        type: type,
        status: false,
      };

      SocketService.emit('producerClosed', data);

      const producer = useProducersStore.getState().producers.get(producer_id);

      // CRITICAL: Stop the producer's track before closing
      // This ensures any cloned tracks release the camera resource
      if (producer?.track) {
        producer.track.stop();
      }

      producer?.close();
      useProducersStore.getState().deleteProducer(producer_id);
      useProducersStore.getState().producerLabel.delete(type);

      if (type !== MEDIA_TYPE.audio) {
        useVideoStreamStore.getState().removeVideo(producer_id);
        if (type === MEDIA_TYPE.video) {
          useVideoStreamStore.getState().setOriginalStream(null);
          useVideoStreamStore.getState().setModifiedStream(null);
        }
        // if (type === mediaType.screen) {
        //   /* if stop sharing remove the stream from store */
        // } else {
        //   useVideoStreamStore.getState().updateVideo(producer_id, { stream: null });
        // }

        //alert(this.pinnedVideoPlayerId + '==' + producer_id);
      }

      if (type === MEDIA_TYPE.audio) {
        useAudioStreamStore.getState().removeAudio(producer_id);
      }

      switch (type) {
        case MEDIA_TYPE.audio:
          currentPeer && setIsAudio(currentPeer?.peer_info?.peer_id, false);
          break;
        case MEDIA_TYPE.video:
          setIsVideo(false);
          break;
        case MEDIA_TYPE.screen:
          setIsScreen(false);
          break;
        default:
          break;
      }

      playSound('left');
    },
    [currentPeer]
  );

  const setIsVideo = useCallback(
    (status: boolean) => {
      if (currentPeer) {
        currentPeer.peer_info.peer_video = status;
        if (!currentPeer.peer_info.peer_video) {
          setVideoOff(currentPeer.peer_info, false);
          sendVideoOff();
        }
      }
    },
    [currentPeer]
  );

  const setIsScreen = useCallback(
    (status: boolean) => {
      if (currentPeer) {
        currentPeer.peer_info.peer_screen = status;
        if (!currentPeer.peer_info.peer_screen && !currentPeer.peer_info.peer_video) {
          setVideoOff(currentPeer.peer_info, false);
          sendVideoOff();
        }
        usePeersStore.getState().updatePeerInfo(currentPeer.peer_info.peer_id, { peer_screen: status });
      }
    },
    [currentPeer]
  );

  const peerAction = useCallback(
    async ({
      from_peer_name,
      id,
      action,
      emit = true,
      broadcast = false,
      info = true,
      msg = '',
      extraFields,
    }: TPeerActionCallbackProps) => {
      const words = id.split('___');
      const peer_id = words[0];

      const { peer_name = '', peer_uuid } = currentPeer?.peer_info || {};
      const remotePeer = peers.get(peer_id);
      if (emit) {
        // send...

        const payload = {
          from_peer_name: peer_name || from_peer_name,
          from_peer_id: currentPeer?.peer_info?.peer_id,
          from_peer_uuid: peer_uuid,
          to_peer_uuid: remotePeer?.peer_uuid ?? '',
          peer_id,
          action,
          message: '',
          broadcast,
          user_type: currentPeer?.peer_info.user_type || 'attendee',
          ...(remotePeer?.record_id && { record_id: remotePeer.record_id }),
          ...extraFields,
        };

        if (!thereAreParticipants && !['ban'].includes(action)) {
          return;
        }
        SocketService.emit(ECommonSocketKeys.PEER_ACTION, payload);
      } else {
        // receive...
        const peerActionAllowed = peer_id === currentPeer?.peer_info?.peer_id || broadcast;

        switch (action) {
          case 'mute':
            if (peerActionAllowed) {
              if (producerExist('audioType') || currentPeer?.peer_info.peer_audio) {
                onMicClick(true);
              }
            }

            break;

          default:
            break;
          //...
        }
      }
    },
    [currentPeer, peers, thereAreParticipants, producerExist, onMicClick]
  );

  return {
    peerAction,
    handleUpdatePeerInfo,
    currentPeer,
    closeProducer,
  };
};

export default usePeerActions;
